import {
  Stack,
  Typography,
  Button,
  useTheme,
  Divider,
  Box,
} from "@mui/material";
import { PrimaryButton } from "@/components/common";
import { ReactNode, useState } from "react";
import { useHealthPlanContractContext } from "@/contexts/HealthPlanContractContext";
import { PostContract } from "@/services/fhir/contract/PostContract";
import { PatchContract } from "@/services/fhir/contract/PatchContract";
import { toast } from "react-toastify";
import { useSessionContext } from "@/contexts/SessionContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { HealthAndSafety } from "@mui/icons-material";
import { HealthPlanModals } from "./HealthPlanModals";
import dayjs from "dayjs";
import { useFormatter } from "@/utils/useFormatter";

interface HealthPlanCardProps {
  title: string;
  description: string;
  status?: string;
  children?: ReactNode;
  priceInfo?: string;
}

export function HealthPlanCard({
  title,
  description,
  status,
  children,
  priceInfo,
}: HealthPlanCardProps) {
  const { access, user } = useSessionContext();
  const { organization } = useOrganizationContext();
  const theme = useTheme();
  const { contractHealthPlan, refetchContract } = useHealthPlanContractContext();
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenActivateModal = () => {
    setIsActivateModalOpen(true);
  };

  const handleCloseActivateModal = () => {
    setIsActivateModalOpen(false);
  };

  const handleOpenCancelModal = () => {
    setIsCancelModalOpen(true);
  };

  const handleCloseCancelModal = () => {
    setIsCancelModalOpen(false);
  };

  const handleActivateHealthPlan = async () => {
    setIsLoading(true);
    try {
      // Gera data no fuso horário local subtraindo 3 horas (UTC-3)
      const currentDate = dayjs().subtract(3, 'hour').toISOString();
      
      await PostContract({
        data: {
          resourceType: "Contract",
          extension: [
            {
              url: "https://fluxmed.com.br/fhir/extension/health_plan/cpf",
              valueString: user?.username || ""
            },
            {
              url: "https://fluxmed.com.br/fhir/extension/health_plan/nome",
              valueString: user?.name || ""
            },
            {
              url: "https://fluxmed.com.br/fhir/extension/health_plan/email",
              valueString: user?.email || ""
            }
          ],
          identifier: [
            {
              system: "https://fluxmed.com.br/hapi/fhir/R5/contract-types",
              value: "healthPlan"
            }
          ],
          status: "executed",
          authority: [
            {
              reference: `Organization/${organization?.id}`
            }
          ],
          type: {
            coding: [
              {
                system: "https://fluxmed.com.br/hapi/fhir/R5/contract-types",
                code: "healthPlan"
              }
            ]
          },
          term: [
            {
              identifier: {
                system: "https://fluxmed.com.br/hapi/fhir/R5/contract-terms",
                value: "healthPlan"
              },
              issued: currentDate,
              applies: {
                start: currentDate
              },
              text: "Contrato de extensão para utilização do plano de saúde",
              offer: {
                type: {
                  coding: [
                    {
                      system: "https://fluxmed.com.br/hapi/fhir/R5/contract-offer-types",
                      code: "subscription"
                    }
                  ]
                }
              },
              asset: [
                {
                  valuedItem: [
                    {
                      entityCodeableConcept: {
                        coding: [
                          {
                            system: "https://fluxmed.com.br/hapi/fhir/R5/subscription-types",
                            code: "subscription-fee"
                          }
                        ]
                      },
                      quantity: {
                        value: 1,
                        unit: "subscription"
                      },
                      unitPrice: {
                        value: 0,
                        currency: "BRL"
                      }
                    }
                  ]
                }
              ]
            }
          ]
        },
      });
      
      toast.success("Extensão de Plano de Saúde ativada com sucesso!");
      await refetchContract();
      setIsActivateModalOpen(false);
    } catch (error) {
      console.error("Erro ao ativar extensão de plano de saúde:", error);
      toast.error("Erro ao ativar extensão. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelHealthPlan = async () => {
    setIsLoading(true);
    try {
      await PatchContract({
        contractId: contractHealthPlan?.id,
        operation: "replace",
        path: "/status",
        content: "cancelled",
      });
      
      toast.success("Extensão de Plano de Saúde cancelada com sucesso!");
      await refetchContract();
      setIsCancelModalOpen(false);
    } catch (error) {
      console.error("Erro ao cancelar extensão de plano de saúde:", error);
      toast.error("Erro ao cancelar extensão. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Stack
        sx={{
          border: "1px solid",
          borderColor: "neutral700.main",
          borderRadius: "15px",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          minHeight: "400px",
          paddingTop: 4,
          paddingBottom: 3,
        }}
      >
        {priceInfo && (
          <Stack
            sx={{
              position: "absolute",
              top: "12px",
              left: "0px",
              bgcolor: "secondary800.main",
              color: "white",
              padding: "4px 12px",
              borderTopRightRadius: "15px",
              borderBottomRightRadius: "15px",
              fontSize: "12px",
              fontWeight: "bold",
              zIndex: 1,
            }}
          >
            {priceInfo}
          </Stack>
        )}
        
        {status && (
          <Stack
            sx={{
              position: "absolute",
              top: "12px",
              right: "0px",
              bgcolor:
                status === "Em desenvolvimento"
                  ? "warning.main"
                  : "success.main",
              color: "white",
              padding: "4px 12px",
              borderTopLeftRadius: "15px",
              borderBottomLeftRadius: "15px",
              fontSize: "12px",
              fontWeight: "bold",
              zIndex: 1,
            }}
          >
            {status}
          </Stack>
        )}

        <Stack
          direction="row"
          width="100%"
          justifyContent="center"
          marginTop="auto"
          paddingTop={2}
        >
          <Typography
            sx={{
              fontSize: "1.25rem",
              fontWeight: "500",
              paddingBottom: 2,
              paddingLeft: 5,
              paddingRight: 5,
              paddingTop: 1,
              alignSelf: "center",
              textAlign: "center",
              position: "relative",
              zIndex: 0,
            }}
          >
            {title}
          </Typography>
        </Stack>

        <Stack flexGrow={1} overflow="auto" maxHeight="330px">
          <Typography
            variant="body1"
            color="neutral700.main"
            sx={{
              paddingBottom: 3,
              paddingLeft: 5,
              paddingRight: 5,
            }}
            align="left"
          >
            {description}
          </Typography>

          {children && (
            <Stack paddingLeft={5} paddingRight={5} paddingBottom={3}>
              {children}
            </Stack>
          )}

          {contractHealthPlan && (
            <Stack paddingLeft={5} paddingRight={5} paddingBottom={3}>
              <Divider sx={{ marginY: 2 }} />
              <Typography
                variant="body1"
                fontWeight="600"
                textAlign="center"
                gutterBottom
              >
                Responsável pela ativação
              </Typography>
              <Box
                sx={{
                  backgroundColor: "neutral100.main",
                  p: 2,
                  borderRadius: 2,
                  mb: 1,
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight="500"
                  sx={{ mb: 1, wordWrap: "break-word", overflow: "hidden" }}
                >
                  <strong>Nome:</strong> {contractHealthPlan?.extension?.find(e => e.url?.includes("nome"))?.valueString || "-"}
                </Typography>
                <Typography variant="body1" fontWeight="500" sx={{ mb: 1 }}>
                  <strong>CPF:</strong> {useFormatter?.formatCPF(contractHealthPlan?.extension?.find(e => e.url?.includes("cpf"))?.valueString || "") || "-"}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="500"
                  sx={{ wordWrap: "break-word", overflow: "hidden" }}
                >
                  <strong>Email:</strong> {contractHealthPlan?.extension?.find(e => e.url?.includes("email"))?.valueString || "-"}
                </Typography>
              </Box>
            </Stack>
          )}
        </Stack>

        <Stack
          direction="row"
          width="100%"
          justifyContent="center"
          marginTop="auto"
          paddingTop={2}
        >
          {contractHealthPlan ? (
            <Button
              sx={{
                width: "70%",
                height: "40px",
                backgroundColor: access?.roles?.includes('owner') ? "error700.main" : "neutral300.main",
                color: "neutral0.main",
                borderRadius: "8px",
                "&:hover": { backgroundColor: access?.roles?.includes('owner') ? "error700.main" : "neutral300.main" },
                textTransform: "capitalize",
                fontSize: theme.typography.subtitle2.fontSize,
                "&:disabled": {
                  color: "neutral600.main",
                },
              }}
              startIcon={<span style={{ color: "white" }}>⚠️</span>}
              disabled={!access?.roles?.includes('owner')}
              onClick={handleOpenCancelModal}
            >
              {access?.roles?.includes('owner') ? "Cancelar Plano de Saúde" : "Somente proprietários"}
            </Button>
          ) : (
            <PrimaryButton
              width="70%"
              height="40px"
              onClick={handleOpenActivateModal}
              startIcon={<HealthAndSafety />}
              disabled={import.meta.env.VITE_ENV === 'production' ? true : !access?.roles?.includes('owner')}
            >
             {access?.roles?.includes('owner') ? "Ativar Plano de Saúde" : "Somente proprietários"}
            </PrimaryButton>
          )}
        </Stack>
      </Stack>

      {/* Modais de Ativação e Cancelamento */}
      <HealthPlanModals
        isActivateModalOpen={isActivateModalOpen}
        isCancelModalOpen={isCancelModalOpen}
        isLoading={isLoading}
        onCloseActivateModal={handleCloseActivateModal}
        onCloseCancelModal={handleCloseCancelModal}
        onConfirmActivate={handleActivateHealthPlan}
        onConfirmCancel={handleCancelHealthPlan}
        agentName={title}
      />
    </>
  );
}
